<template>
  <div class="highchart-container">
    <div :id="domId" style="width: 100%; height: 100%"></div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  watch,
  nextTick,
  onBeforeUnmount,
  onUpdated,
} from "vue";
import Highcharts from "highcharts";
import loadFunnel from "highcharts/modules/funnel";


loadFunnel(Highcharts);

// Props
const props = defineProps({
  domId: {
    type: String,
    required: true,
  },
  seriesData: {
    type: Array,
    required: true,
    default: () => [],
  },
  colour: {
    type: Array,
    required: true,
    default: () => [
      "#10B981",
      "#3B82F6",
      "#8B5CF6",
      "#EC4899",
      "#06B6D4",
      "#9CA3AF",
    ],
  },
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  height: {
    type: Number,
    default: 250,
  },
  chartType: {
    type: String,
    default: "pie",
    validator: (value) => ["pie", "donut"].includes(value),
  },
  innerSize: {
    type: String,
    default: "0%",
  },
  showDataLabels: {
    type: Boolean,
    default: true,
  },
  dataLabelFormat: {
    type: String,
    default: "<b>{point.name}</b>: {point.y}",
  },
  showLegend: {
    type: Boolean,
    default: false,
  },
  legendPosition: {
    type: String,
    default: "bottom",
    validator: (value) => ["right", "bottom", "top", "left"].includes(value),
  },
  centerText: {
    type: String,
    default: "",
  },
  centerValue: {
    type: String,
    default: "",
  },
});

// Emits
const emit = defineEmits(["chartClick", "legendClick", "chartReady"]);

// Refs
let chartInstance = null;

// Methods
const renderChart = async () => {
  // Wait for DOM to be ready
  await nextTick();

  // Check if container exists
  const container = document.getElementById(props.domId);
  if (!container) {
    console.warn(`Chart container with ID "${props.domId}" not found`);
    return;
  }

  // Ensure container has dimensions
  const containerRect = container.getBoundingClientRect();
  if (containerRect.width === 0 || containerRect.height === 0) {
    console.warn("Chart container has no dimensions, retrying...");
    setTimeout(renderChart, 100);
    return;
  }

  // Check if data is available - add null checks
  if (
    !props.seriesData ||
    !Array.isArray(props.seriesData) ||
    props.seriesData.length === 0
  ) {
    console.warn("No valid chart data available");
    return;
  }

  // Check if colours array exists
  if (
    !props.colour ||
    !Array.isArray(props.colour) ||
    props.colour.length === 0
  ) {
    console.warn("No valid colour array provided");
    return;
  }

  // Destroy existing chart
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  try {
    // Determine if this should be a donut chart
    const isDonut = props.chartType === "donut" || props.innerSize !== "0%";
    const innerSize = isDonut ? props.innerSize || "60%" : "0%";

    // Legend configuration
    const legendAlign =
      props.legendPosition === "right" || props.legendPosition === "left"
        ? props.legendPosition
        : "center";

    const legendVerticalAlign =
      props.legendPosition === "top" || props.legendPosition === "bottom"
        ? props.legendPosition === "top"
          ? "top"
          : "bottom"
        : "middle";

    const legendLayout =
      props.legendPosition === "right" || props.legendPosition === "left"
        ? "vertical"
        : "horizontal";

    chartInstance = Highcharts.chart(props.domId, {
      chart: {
        type: "pie",
        height: props.height,
        backgroundColor: "transparent",
        spacing: [10, 10, 10, 10],
        animation: {
          duration: 1000,
        },
        events: {
          load: function () {
            if (isDonut && (props.centerText || props.centerValue)) {
              addCenterText(this);
            }
            emit("chartReady", this);
          },
          redraw: function () {
            if (isDonut && (props.centerText || props.centerValue)) {
              addCenterText(this);
            }
          },
        },
      },
      title: {
        text: props.title,
        style: {
          fontSize: "15px",
          fontWeight: "600",
          color: "#1F2937",
          fontFamily: "poppins, sans-serif; ",
        },
      },
      subtitle: {
        text: props.subtitle,
        style: {
          fontSize: "14px",
          color: "#6B7280",
          fontFamily: "poppins, sans-serif; ",
        },
      },
      tooltip: {
        formatter: function () {
          // Round value and add thousands separators
          const value = Math.round(this.point.y).toLocaleString();
          const percentage = this.point.percentage.toFixed(1);

          return `<b>${this.point.name}</b><br> ${this.series.name}: <b>${value}</b> (${percentage}%)`;
        },
        style: {
          fontSize: "12px",
          fontFamily: "poppins, sans-serif",
        },
      },

      accessibility: {
        point: {
          valueSuffix: "%",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          colors: props.colour,
          innerSize: innerSize,
          borderWidth: 0,
          animation: {
            duration: 1000,
          },
          dataLabels: {
            enabled: props.showDataLabels,
            format: props.dataLabelFormat,
            style: {
              fontSize: "12px",
              fontWeight: "500",
              color: "#374151",
            },
          },
          showInLegend: props.showLegend,
          states: {
            hover: {
              halo: {
                size: 0,
              },
              brightness: 0.1,
            },
          },
          point: {
            events: {
              click: function (e) {
                emit("chartClick", {
                  point: this,
                  event: e,
                });
              },
              legendItemClick: function (e) {
                emit("legendClick", {
                  point: this,
                  event: e,
                });
                return false; // Prevent default legend behavior
              },
            },
          },
        },
      },
      legend: {
        enabled: props.showLegend,
        align: legendAlign,
        verticalAlign: legendVerticalAlign,
        layout: legendLayout,
        itemStyle: {
          fontSize: "12px",
          fontWeight: "400",
          color: "#6B7280",
        },
        itemHoverStyle: {
          color: "#374151",
        },
        symbolRadius: 6,
        symbolHeight: 12,
        symbolWidth: 12,
        itemMarginBottom: 8,
        itemMarginRight: props.legendPosition === "right" ? 0 : 15,
      },
      series: [
        {
          name: "Amount",
          colorByPoint: true,
          data: props.seriesData.map((item, index) => ({
            ...item,
            color:
              item.color ||
              (props.colour && props.colour[index % props.colour.length]) ||
              "#10B981",
          })),
        },
      ],
      credits: {
        enabled: false,
      },
    });
  } catch (error) {
    console.error("Error creating chart:", error);
  }
};

const addCenterText = (chart) => {
  if (!props.centerText && !props.centerValue) return;

  try {
    // Remove existing center text
    if (chart.centerTextGroup) {
      chart.centerTextGroup.destroy();
    }

    const centerX = chart.plotLeft + chart.plotWidth / 2;
    const centerY = chart.plotTop + chart.plotHeight / 2;

    chart.centerTextGroup = chart.renderer.g("center-text").add();

    if (props.centerText) {
      chart.renderer
        .text(props.centerText, centerX, centerY - 10)
        .css({
          fontSize: "12px",
          color: "#6B7280",
          fontWeight: "400",
          textAnchor: "middle",
          fontFamily: "poppins, sans-serif; ",
        })
        .attr({
          align: "center",
        })
        .add(chart.centerTextGroup);
    }

    if (props.centerValue) {
      chart.renderer
        .text(props.centerValue, centerX, centerY + 15)
        .css({
          fontSize: "20px",
          color: "#1F2937",
          fontWeight: "600",
          textAnchor: "middle",
          textWrap: "wrap",
          fontFamily: "poppins, sans-serif; ",
        })
        .attr({
          align: "center",
        })
        .add(chart.centerTextGroup);
    }
  } catch (error) {
    console.error("Error adding center text:", error);
  }
};

const updateChart = () => {
  if (
    chartInstance &&
    props.seriesData &&
    Array.isArray(props.seriesData) &&
    props.seriesData.length > 0
  ) {
    try {
      chartInstance.series[0].setData(
        props.seriesData.map((item, index) => ({
          ...item,
          color:
            item.color ||
            (props.colour && props.colour[index % props.colour.length]) ||
            "#10B981",
        })),
        true, // redraw
        true // animation
      );
    } catch (error) {
      console.error("Error updating chart:", error);
      // If update fails, recreate the chart
      renderChart();
    }
  }
};

const resizeChart = () => {
  if (chartInstance) {
    try {
      chartInstance.reflow();
    } catch (error) {
      console.error("Error resizing chart:", error);
    }
  }
};

// Watchers
watch(
  () => props.seriesData,
  () => {
    if (chartInstance) {
      updateChart();
    } else {
      renderChart();
    }
  },
  { deep: true }
);

watch(
  () => props.colour,
  () => {
    renderChart();
  },
  { deep: true }
);

watch([() => props.title, () => props.subtitle], () => {
  renderChart();
});

watch([() => props.centerText, () => props.centerValue], () => {
  if (chartInstance) {
    addCenterText(chartInstance);
  }
});

// Lifecycle
onMounted(async () => {
  // Wait for component to be fully mounted
  await nextTick();

  // Add a small delay to ensure DOM is ready
  setTimeout(() => {
    renderChart();
  }, 50);

  // Add resize listener
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  try {
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
  } catch (error) {
    console.error("Error destroying chart:", error);
  }

  window.removeEventListener("resize", resizeChart);
});

// Expose methods for parent component
defineExpose({
  resizeChart,
  updateChart,
  renderChart,
  chartInstance: () => chartInstance,
});
</script>

<style scoped>
.highchart-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Highcharts custom styling */
:deep(.highcharts-container) {
  font-family: inherit !important;
}

:deep(.highcharts-title) {
  font-family: inherit !important;
}

:deep(.highcharts-subtitle) {
  font-family: inherit !important;
}

:deep(.highcharts-legend-item text) {
  fill: #6b7280 !important;
  font-size: 12px !important;
}

:deep(.highcharts-legend-item:hover text) {
  fill: #374151 !important;
}

:deep(.highcharts-pie-series .highcharts-point) {
  stroke-width: 0;
}

:deep(.highcharts-pie-series .highcharts-point:hover) {
  filter: brightness(1.1);
}

:deep(.highcharts-tooltip) {
  border-radius: 8px !important;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

:deep(.highcharts-data-label) {
  font-family: inherit !important;
}
:deep(.highcharts-legend-item) {
  display: inline-flex !important;
  white-space: nowrap !important;
}
</style>
